import type { CalendlyAnswer } from "./calendly";

export type Objection =
  | "Prix"
  | "Pas encore de clients"
  | "Formation seule"
  | "Métier / présentiel"
  | "Éthique"
  | "Confiance / récurrence"
  | "Garantie";

/** Minuscules, sans accents, apostrophes typographiques ramenées à `'`. */
function norm(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\u2018\u2019`\u00b4]/g, "'")
    .toLowerCase();
}

// Appliquées au texte normalisé (chat et réponses Calendly). Ordre = ordre de sortie.
const KEYWORD_RULES: { objection: Objection; re: RegExp }[] = [
  { objection: "Prix", re: /\b(?:prix|tarifs?|couts?|combien|paiements?)\b/ },
  { objection: "Pas encore de clients", re: /\bje debute\b|\bpas encore lance/ },
  { objection: "Formation seule", re: /\bseulement\s+(?:\w+\s+)?formation\b|\ba part\b/ },
  {
    objection: "Métier / présentiel",
    re: /\bpresentiel|\bsophro|\btherapeutes?\b|\bvisio/,
  },
  { objection: "Éthique", re: /\bethiques?\b|\bvulnerables?\b/ },
  {
    objection: "Confiance / récurrence",
    re: /nouveaux chaque mois|retrouver de nouveaux|ne connai(?:t|s) pas|\bengager un an\b/,
  },
  { objection: "Garantie", re: /\bgaranti(?:e|es|s|t)?\b/ },
];

const PAYING_CLIENTS_Q = /\bclients? payants?\b/;
const NOT_YET_A = /^pas encore\b/;

/** Objections détectées, dans l'ordre du type `Objection`, sans doublon. */
export function detectObjections(input: {
  chat: string[];
  calendly: CalendlyAnswer[];
}): Objection[] {
  const found = new Set<Objection>();
  const texts = [...input.chat, ...input.calendly.map((c) => c.a)].map(norm);

  for (const t of texts) {
    for (const { objection, re } of KEYWORD_RULES) if (re.test(t)) found.add(objection);
  }
  for (const { q, a } of input.calendly) {
    if (PAYING_CLIENTS_Q.test(norm(q)) && NOT_YET_A.test(norm(a).trim())) {
      found.add("Pas encore de clients");
    }
  }

  const order: Objection[] = [
    "Prix",
    "Pas encore de clients",
    "Formation seule",
    "Métier / présentiel",
    "Éthique",
    "Confiance / récurrence",
    "Garantie",
  ];
  return order.filter((o) => found.has(o));
}
